/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import React from 'react';
import { ROUTES, useRouter } from '@components/service-and-repair/providers/router';
import { Analytics } from '@services/analytics-service';
import { datadogRum } from '@datadog/browser-rum';
import { useExperiment } from '@carvana/core-experiment';
import { AnalyticEvents, Experiments } from '@constants/index';
import { UserSelectionsType } from '@components/service-and-repair/providers/user-selections';
import {
  isCovered100DayLimitedWarrantyAndWithinXDays,
  isCovered100DayLimitedWarrantyWithin30DaysAndInCalifornia,
  isCovered30Days,
  isSubCategoryIncluded,
  isUserWithinXDays,
} from '@store/sar-store';
import { CoverageScreenType } from './get-logic/coverage-type';
import { aOrAn } from '../../utilities';
import { Category, SubCategory } from '../../constants';
import { generateKey } from './coverage-screen';
import styles from './coverage.module.css';

type DescriptionDisplayProps = {
  coverageScreenType: CoverageScreenType;
  make: string;
  selectedWarranty: {
    deductible?: {
      inNetwork: number | undefined;
      outOfNetwork: number | undefined;
    };
    daysLeft: number;
  };
  userSelections: UserSelectionsType;
  assignedBucket?: string;
  carvanaCare: string | null;
};

export const DescriptionDisplay = ({
  coverageScreenType,
  make,
  selectedWarranty,
  userSelections,
  carvanaCare,
}: DescriptionDisplayProps) => {
  const { goToRoute } = useRouter();
  const { assignedBucket } = useExperiment(Experiments.INF_ONF_DEDUCTIBLE);

  const deductibleDifference =
    (selectedWarranty?.deductible?.outOfNetwork ?? 350) - (selectedWarranty?.deductible?.inNetwork ?? 100);
  const isThereADeductibleDifference = !!(
    (Number(selectedWarranty?.deductible?.outOfNetwork) || 0) === Number(selectedWarranty?.deductible?.inNetwork) || 0
  );
  const showAsterisk =
    isCovered100DayLimitedWarrantyAndWithinXDays(14) || isCovered100DayLimitedWarrantyWithin30DaysAndInCalifornia();
  const showDoubleAsterisk =
    isUserWithinXDays(14) && isCovered30Days() && isSubCategoryIncluded(userSelections?.subcategory);

  const handleBenefitsClick = () => {
    Analytics.trackEvent(AnalyticEvents.sarCoverageScreenClickedBenefits);
    datadogRum.addAction(AnalyticEvents.sarCoverageScreenClickedBenefits);
    goToRoute(ROUTES.BENEFITS);
  };

  const defaultCoveredDescription = (assignedBucket) =>
    assignedBucket === 'treatment'
      ? [
          <>
            <p>
              Schedule an appointment with an in-network repair shop to diagnose your issue. They&apos;ll work with our
              coverage provider, SilverRock™, to confirm your repairs are covered.
            </p>
            <>
              In-network deductible:{' '}
              <b>
                ${selectedWarranty.deductible?.inNetwork}
                {showDoubleAsterisk ? '**' : showAsterisk && '*'}
              </b>
            </>
            {!isThereADeductibleDifference ? (
              <div className={styles['deductible-difference']}>
                Save ${deductibleDifference} on your deductible by choosing an in-network shop.{' '}
                <a onClick={handleBenefitsClick}>See in-network benefits</a>
              </div>
            ) : (
              <div className={styles['see-benefits']}>
                <a onClick={handleBenefitsClick}>See in-network benefits</a>
              </div>
            )}
          </>,
        ]
      : [
          <>
            Schedule an appointment with an in-network repair shop to diagnose your issue.
            {isThereADeductibleDifference
              ? ` There's a $${selectedWarranty?.deductible?.inNetwork} deductible by using an in-network shop for covered repairs. `
              : ` Save $${deductibleDifference} on your deductible by using an in-network shop for covered repairs. `}
            <a className={styles['benefits-link']} onClick={handleBenefitsClick}>
              See more in-network benefits.
            </a>
          </>,
          `They'll work with our coverage provider, SilverRock™, to confirm that your repairs are covered.`,
        ];

  const baseText = () => [`Get service at a recommended shop or find one that works for you.`];

  const CoverageScreenDescription = {
    contactUs: () => ['One of our Advocates may be able to resolve your issue.'],
    limited100Covered: (assignedBucket) => defaultCoveredDescription(assignedBucket),
    manufacturerCovered: (make: string) => [
      `Schedule an appointment with ${aOrAn(make)} ${make} repair center to get a diagnostic.`,
    ],
    carvanaCareCovered: (assignedBucket) => defaultCoveredDescription(assignedBucket),
    carvanaCareRoadHazardCovered: () => [
      'Schedule an appointment with an in-network repair shop to assess your damage. They’ll work with our coverage provider, SilverRock™, to confirm your damage is covered.',
      <>
        <a className={styles['benefits-link']} onClick={handleBenefitsClick}>
          See more in-network benefits.
        </a>
      </>,
    ],
    carvanaCareRoadHazardNotCovered: () => [
      `Your ${carvanaCare} coverage only covers damage caused by road debris, a curb or pothole, otherwise known as “road hazard” damage. It does not cover normal wear or tear.`,
      'Get service at a recommended shop or find one that works for you.',
    ],
    carvanaCareOilChangeCovered: () => [
      "Service must be done at an in-network shop for coverage to apply. Schedule an appointment with the shop and they'll work with our coverage provider, SilverRock™, to process the repair.",
      <>
        <a className={styles['benefits-link']} onClick={handleBenefitsClick}>
          See more in-network benefits.
        </a>
      </>,
    ],
    carvanaCareOilChangeNotCovered: () => [
      `Maintenance like oil changes, tire rotations, wiper blade and fluid replacements are excluded.`,
      'Get service at a recommended shop or find one that works for you.',
    ],
    carvanaCareDentCovered: (assignedBucket) => defaultCoveredDescription(assignedBucket),
    notCovered: (userSelections: UserSelectionsType) => {
      if (userSelections.category === Category.Battery)
        return [
          'Wearable components like tires, brakes or batteries are excluded.',
          'Get service at a recommended shop or find one that works for you.',
        ];
      if (userSelections.category === Category.Maintenance)
        return [
          `Maintenance like oil changes, tire rotations, wiper blade and fluid replacements are excluded.`,
          'Get service at a recommended shop or find one that works for you.',
        ];
      if (userSelections.category === Category.TiresOrBrakes) {
        return [
          'Wearable components like tires, brakes or batteries are excluded.',
          'Get service at a recommended shop or find one that works for you.',
        ];
      }

      return baseText();
    },
    thirtyDayCovered: (date: string, subCategory, assignedBucket?: string) => {
      const showAsterisk =
        assignedBucket === 'treatment' && isCovered30Days() && isSubCategoryIncluded(userSelections?.subcategory);

      return [
        <>
          {subCategory === SubCategory.Brakes && (
            <p>
              Brake issues are only covered if they&apos;re safety related, such as perforated rust.
              {showAsterisk && '*'}
            </p>
          )}
          {subCategory === SubCategory.Tires && (
            <p>
              Tire issues are only covered if they&apos;re safety related, such as treadless tire.{showAsterisk && '*'}
            </p>
          )}
          {subCategory === SubCategory.Wheels && (
            <p>
              Wheel issues are only covered if they&apos;re safety related, such as cracked or bent rim.
              {showAsterisk && '*'}
            </p>
          )}
        </>,
        ...[
          assignedBucket === 'treatment' ? (
            <>
              <p>
                Schedule an appointment with an in-network repair shop to diagnose your issue. They&apos;ll work with
                our coverage provider, SilverRock™, to confirm your repairs are covered.
              </p>
              <p>
                In-network deductible:{' '}
                <b>
                  ${selectedWarranty.deductible?.inNetwork}
                  {showDoubleAsterisk ? '**' : showAsterisk && '*'}
                </b>
              </p>
              {!isThereADeductibleDifference ? (
                <div className={styles['deductible-difference']}>
                  Save ${deductibleDifference} on your deductible by choosing an in-network shop.{' '}
                  <a onClick={handleBenefitsClick}>See in-network benefits</a>
                </div>
              ) : (
                <div className={styles['see-benefits']}>
                  <a onClick={handleBenefitsClick}>See in-network benefits</a>
                </div>
              )}
            </>
          ) : (
            <>
              <>
                You can schedule an appointment with an in-network repair shop to diagnose your issue by <b>{date}</b>.
              </>

              {isThereADeductibleDifference
                ? ` There's a $${selectedWarranty?.deductible?.inNetwork} deductible by using an in-network shop for covered repairs. `
                : ` Save $${deductibleDifference} on your deductible by using an in-network shop for covered repairs. `}
              <a className={styles['benefits-link']} onClick={handleBenefitsClick}>
                See more in-network benefits.
              </a>
              <p>They&apos;ll work with our coverage provider, SilverRock™, to confirm your repairs are covered.</p>
            </>
          ),
        ],
      ];
    },
    carvanaCareOilTireCovered: (userSelections: UserSelectionsType) => {
      if (
        (userSelections.category === Category.TiresOrBrakes && userSelections.subcategory === SubCategory.Tires) ||
        userSelections.subcategory === SubCategory.Wheels
      ) {
        if (userSelections.subsubcategory === 'Yes') {
          return [
            'Schedule an appointment with an in-network repair shop to assess your damage. They’ll work with our coverage provider, SilverRock™, to confirm your damage is covered.',
            <>
              <a className={styles['benefits-link']} onClick={handleBenefitsClick}>
                See more in-network benefits.
              </a>
            </>,
          ];
        }
      }
      return [
        "Service must be done at an in-network shop for coverage to apply. Schedule an appointment with the shop and they'll work with our coverage provider, SilverRock™, to process the repair.",
        <>
          <a className={styles['benefits-link']} onClick={handleBenefitsClick}>
            See more in-network benefits.
          </a>
        </>,
      ];
    },
    conditionallyCoveredBrakes: () => [
      'Wearable components such as brake pads and rotors are excluded for your coverage.',
      <>
        Schedule an appointment with an in-network repair shop to diagnose your issue.
        {isThereADeductibleDifference
          ? ` There's a $${selectedWarranty?.deductible?.inNetwork} deductible by using an in-network shop for covered repairs. `
          : ` Save $${deductibleDifference} on your deductible by using an in-network shop for covered repairs. `}
        <a className={styles['benefits-link']} onClick={handleBenefitsClick}>
          See more in-network benefits.
        </a>
      </>,
      `They'll work with our coverage provider, SilverRock™, to verify your issue and make a determination.`,
    ],
    notCoveredCosmetic: () => [...baseText()],
    notCoveredGlass: () => ['Get service at the recommended business or find one that works for you.'],
    notCoveredAdditionalCosmetic: () => [`You've submitted the max amount of cosmetic concerns.`, ...baseText()],
    notCoveredAdditionalKey: () => [`Get service at the recommended business.`],
    notCoveredMissingAccessories: (make: string) => [
      `We recommend visiting ${make}'s website or an auto-parts store to replace any missing accessories.`,
    ],
    cosmeticSubmitted: () => ['We will email you within 48 hours with next steps.'],
  };

  const getDescription = () => {
    if (
      coverageScreenType === CoverageScreenType.manufacturerCovered ||
      coverageScreenType === CoverageScreenType.notCoveredMissingAccessories
    ) {
      return CoverageScreenDescription[coverageScreenType](make)?.filter(Boolean);
    }
    if (coverageScreenType === CoverageScreenType.thirtyDayCovered) {
      const expirationDate = new Date(new Date().setDate(new Date().getDate() + selectedWarranty?.daysLeft));
      return CoverageScreenDescription[coverageScreenType](
        expirationDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        userSelections?.subcategory,
        assignedBucket
      )?.filter(Boolean);
    }
    if (coverageScreenType === CoverageScreenType.notCovered) {
      return CoverageScreenDescription[coverageScreenType](userSelections)?.filter(Boolean);
    }

    if (coverageScreenType === CoverageScreenType.carvanaCareOilTireCovered) {
      return CoverageScreenDescription[coverageScreenType](userSelections)?.filter(Boolean);
    }

    return CoverageScreenDescription[coverageScreenType](userSelections)?.filter(Boolean);
  };

  const descriptionContent = getDescription();

  return (
    <>
      {descriptionContent?.map((desc) => (
        <p key={generateKey()} className={styles['paragraph']}>
          {desc}
        </p>
      ))}
    </>
  );
};
